/**
 * The browser build's half of the HttpOnly refresh-token cookie (the server's half is RefreshCookieInterceptor).
 *
 * Wraps fetch for the /auth routes only: it asks the server to also keep the refresh token in an HttpOnly cookie
 * (header + credentials), and when a response carries a refresh token it checks that the browser really kept the
 * cookie (GET /auth/cookie-check). If it did, the app is handed the placeholder "cookie" instead of the real token, so
 * the token never reaches page scripts or localStorage, and the server reads the cookie when the app sends the
 * placeholder back. If the browser did not keep it (Safari and others block a cookie from another site), the real token
 * is passed through untouched and the app behaves exactly as before: nobody is signed out by this.
 */
export const REFRESH_PLACEHOLDER = 'cookie';

type FetchFn = typeof fetch;

/** Swaps the first string `refreshToken` (at most two levels in) for the placeholder; true when there was one. */
function hideToken(value: unknown, depth = 0): boolean {
  if (!value || typeof value !== 'object' || depth > 2) return false;
  const record = value as Record<string, unknown>;
  if (typeof record.refreshToken === 'string' && record.refreshToken) {
    record.refreshToken = REFRESH_PLACEHOLDER;
    return true;
  }
  return Object.values(record).some((child) => hideToken(child, depth + 1));
}

export function createRefreshCookieFetch(nativeFetch: FetchFn, apiBase: string): FetchFn {
  const authBase = `${apiBase}/auth/`;
  let cookieWorks = false;

  const checkCookie = async (): Promise<boolean> => {
    try {
      const res = await nativeFetch(`${authBase}cookie-check`, { credentials: 'include', headers: { 'x-refresh-cookie': '1' } });
      return res.ok && ((await res.json()) as { cookie?: boolean }).cookie === true;
    } catch {
      return false;
    }
  };

  return async (input, init) => {
    const isRequest = typeof Request !== 'undefined' && input instanceof Request;
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
    if (!url.startsWith(authBase) || url.startsWith(`${authBase}cookie-check`)) return nativeFetch(input, init);

    const headers = new Headers(init?.headers ?? (isRequest ? (input as Request).headers : undefined));
    headers.set('x-refresh-cookie', '1');
    const response = isRequest
      ? await nativeFetch(new Request(input as Request, { ...init, headers, credentials: 'include' }))
      : await nativeFetch(input, { ...init, headers, credentials: 'include' });

    if (!response.ok || !(response.headers.get('content-type') ?? '').includes('json')) return response;
    let data: unknown;
    try {
      data = await response.clone().json();
    } catch {
      return response;
    }
    // Only a response that carries a token matters. Once the cookie is known to work it is not asked again.
    if (!hideToken(JSON.parse(JSON.stringify(data)))) return response;
    if (!cookieWorks) cookieWorks = await checkCookie();
    if (!cookieWorks) return response;

    hideToken(data);
    const out = new Headers(response.headers);
    out.delete('content-length');
    out.delete('content-encoding');
    return new Response(JSON.stringify(data), { status: response.status, statusText: response.statusText, headers: out });
  };
}
